import { type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { Icon } from '../components/Icon'
import { PageBand, PageShell } from '../components/PageShell'

type SentState = { reference?: string; specialistName?: string }

/**
 * Confirmation after an appointment REQUEST is sent (M14-T08, ADR-0029). The
 * appointment is NOT booked — AMTC calls back to confirm. The copy must say so;
 * a patient may act on this screen, so it must not promise a confirmed slot.
 */
export function AppointmentRequestSent(): ReactNode {
  const navigate = useNavigate()
  const state = (useLocation().state ?? {}) as SentState

  return (
    <PageShell measure={594}>
      <PageBand
        className="flex flex-1 flex-col px-6 py-16"
        contentClassName="flex flex-1 flex-col items-center justify-center gap-3 text-center"
      >
        <span className="grid h-14 w-14 place-items-center rounded-full bg-bed-good-bg text-bed-good">
          <Icon name="check" size={26} />
        </span>
        <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
          Request sent
        </h1>
        <p className="max-w-sm text-[13px] leading-relaxed text-ink-2">
          The AMTC team will call you to confirm your appointment
          {state.specialistName ? ` with ${state.specialistName}` : ''}.
          You&apos;ll get a message with the details once it&apos;s arranged.
        </p>
        {state.reference && (
          <p className="rounded-full bg-bg-sunken px-3 py-1 text-[13px] font-medium text-ink-1">
            Reference: {state.reference}
          </p>
        )}
        <div className="mt-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate('/appointments')}
            className="rounded-r3 bg-accent px-5 py-2 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            View my requests
          </button>
          <button
            type="button"
            onClick={() => navigate('/specialists')}
            className="rounded-r3 border border-line-2 px-5 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to specialists
          </button>
        </div>
      </PageBand>
    </PageShell>
  )
}
